import { useEffect, useRef, useState } from 'react';

/**
 * Hook to detect when an element intersects with the viewport using Intersection Observer API.
 * Once intersection is detected, the observer disconnects automatically.
 *
 * @param options - IntersectionObserver options
 * @returns [isIntersecting, elementRef] - Boolean indicating intersection state and ref to attach to element
 *
 * @example
 * Lazy load when element enters viewport:
 *   const [isVisible, ref] = useIntersectionObserver();
 *
 * Lazy load 100px before element enters viewport:
 *   const [isVisible, ref] = useIntersectionObserver({
 *     rootMargin: '100px 0px 0px 0px',
 *   });
 *
 * Lazy load when 50% of element is visible:
 *   const [isVisible, ref] = useIntersectionObserver({
 *     threshold: 0.5,
 *   });
 */
export default function useIntersectionObserver(
  options: IntersectionObserverInit = {},
): [boolean, React.RefObject<HTMLDivElement>] {
  const [isIntersecting, setIsIntersecting] = useState(false);
  const elementRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setIsIntersecting(true);
          observer.disconnect();
        }
      },
      options,
    );

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => observer.disconnect();
  }, [options]);

  return [isIntersecting, elementRef];
}
